import { CaixaArredondada } from '../componentes/CaixaArredondada'
import type { ProporcoesVitima } from '../../dominio/didatico/perfisVitima'
import { geometriaDaMao, geometriasDoRosto } from './corpoHumano'

/**
 * Detalhes que tiram do manequim a leitura de "linguiça": rosto, cabelo, mãos e
 * calçado. São malhas pequenas e de poucos triângulos, sem textura, e não mudam
 * nenhuma articulação: cada uma é filha do segmento a que pertence.
 */

/** Translucidez herdada do fantasma e do Raio-X: os detalhes acompanham o corpo. */
export interface Casca {
  readonly transparent: boolean
  readonly opacity: number
}

const COR_OLHO = '#16181d'

/** Cabelo por perfil: o idoso é grisalho; os demais, castanho escuro. */
export function corDoCabelo(id: ProporcoesVitima['id']): string {
  return id === 'idoso' ? '#c4c8cf' : '#2b1d14'
}

interface RostoProps {
  raioCabeca: number
  corPele: string
  corCabelo: string
  /** cabelo comprido, atrás da cabeça */
  cabeloLongo?: boolean
  casca: Casca
}

/** Cabelo, orelhas, nariz e olhos de uma cabeça de raio `raioCabeca` (origem no centro da cabeça). */
export function Rosto3D({ raioCabeca, corPele, corCabelo, cabeloLongo = false, casca }: RostoProps) {
  const { pele, olhos, cabelo } = geometriasDoRosto(raioCabeca, cabeloLongo)
  return (
    <group>
      <mesh geometry={cabelo}>
        <meshStandardMaterial color={corCabelo} roughness={0.85} {...casca} />
      </mesh>
      <mesh geometry={pele}>
        <meshStandardMaterial color={corPele} roughness={0.65} {...casca} />
      </mesh>
      <mesh geometry={olhos}>
        <meshStandardMaterial color={COR_OLHO} roughness={0.4} {...casca} />
      </mesh>
    </group>
  )
}

interface MaoProps {
  /** `raioMembro` do perfil: a mão cresce com ele */
  raioMembro: number
  cor: string
  /** −1 esquerdo, 1 direito: o polegar fica do lado de dentro, voltado para a frente */
  lado: -1 | 1
  casca: Casca
}

/** Mão: palma achatada e polegar, com a origem no punho e os dedos para baixo (−y). */
export function Mao3D({ raioMembro, cor, lado, casca }: MaoProps) {
  return (
    <mesh geometry={geometriaDaMao(raioMembro, lado)}>
      <meshStandardMaterial color={cor} roughness={0.65} {...casca} />
    </mesh>
  )
}

interface CalcadoProps {
  raioMembro: number
  corCalcado: string
  corSola: string
  casca: Casca
}

/**
 * Tênis, articulado no tornozelo (origem). A base da sola fica 4,5 cm abaixo do
 * tornozelo, como o pé de caixa que ele substitui, para continuar apoiado sobre o
 * assoalho sem mudar a pose.
 */
export function Calcado3D({ raioMembro: r, corCalcado, corSola, casca }: CalcadoProps) {
  const largura = r * 1.75
  const comprimento = r * 3.5
  const centro = comprimento * 0.27
  return (
    <group>
      <mesh position={[0, -0.012, centro + comprimento * 0.02]}>
        <CaixaArredondada args={[largura, 0.056, comprimento * 0.94]} raio={0.02} />
        <meshStandardMaterial color={corCalcado} roughness={0.75} {...casca} />
      </mesh>
      <mesh position={[0, -0.035, centro]}>
        <CaixaArredondada args={[largura * 1.04, 0.02, comprimento]} raio={0.008} />
        <meshStandardMaterial color={corSola} roughness={0.9} {...casca} />
      </mesh>
    </group>
  )
}
